import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { serviceCardsApi, servicesApi } from '../lib/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const ServiceCardForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title_en: '',
    title_ar: '',
    excerpt_en: '',
    excerpt_ar: '',
    service_id: '',
  });

  const { data: serviceCard, isLoading } = useQuery({
    queryKey: ['service-cards', id],
    queryFn: () => serviceCardsApi.getById(id!).then((res: any) => res.data),
    enabled: isEdit,
  });

  const { data: services = [] } = useQuery({
    queryKey: ['services', i18n.language],
    queryFn: async () => {
      const response = await servicesApi.getAll(i18n.language);
      // Handle new response structure: { status: "success", data: [...] }
      // or fallback to old structure
      if (response.data?.status === 'success' && Array.isArray(response.data?.data)) {
        return response.data.data;
      }
      // Fallback for old structure or direct data
      return response.data?.data || response.data || [];
    },
  });

  useEffect(() => {
    if (serviceCard && isEdit) {
      setFormData({
        title_en: serviceCard.title_en || '',
        title_ar: serviceCard.title_ar || '',
        excerpt_en: serviceCard.excerpt_en || '',
        excerpt_ar: serviceCard.excerpt_ar || '',
        service_id: serviceCard.service_id || '',
      });
    }
  }, [serviceCard, isEdit]);

  const createMutation = useMutation({
    mutationFn: (data: any) => serviceCardsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-cards'] });
      toast.success(t('serviceCardCreated'));
      navigate('/service-cards');
    },
    onError: () => {
      toast.error(t('errorOccurred'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => serviceCardsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-cards'] });
      toast.success(t('serviceCardUpdated'));
      navigate('/service-cards');
    },
    onError: () => {
      toast.error(t('errorOccurred'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleServiceChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      service_id: value,
    }));
  };

  if (isLoading && isEdit) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4 rtl:space-x-reverse">
        <Button variant="outline" onClick={() => navigate('/service-cards')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {i18n.language === 'ar' ? 'العودة' : 'Back'}
        </Button>
        <h1 className="text-3xl font-bold">
          {isEdit 
            ? (i18n.language === 'ar' ? 'تعديل كرت الخدمة' : 'Edit Service Card')
            : (i18n.language === 'ar' ? 'إضافة كرت خدمة جديد' : 'Add New Service Card')
          }
        </h1>
      </div>

      <form onSubmit={handleSubmit} className={`space-y-6 ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>{i18n.language === 'ar' ? 'المعلومات الأساسية' : 'Basic Information'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="title_en">{i18n.language === 'ar' ? 'العنوان (إنجليزي)' : 'Title (English)'}</Label>
                <Input
                  id="title_en"
                  name="title_en"
                  value={formData.title_en}
                  onChange={handleChange}
                  required
                  dir="ltr"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title_ar">{i18n.language === 'ar' ? 'العنوان (عربي)' : 'Title (Arabic)'}</Label>
                <Input
                  id="title_ar"
                  name="title_ar"
                  value={formData.title_ar}
                  onChange={handleChange}
                  required
                  dir="rtl"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Excerpt */}
        <Card>
          <CardHeader>
            <CardTitle>{i18n.language === 'ar' ? 'الملخص' : 'Excerpt'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="excerpt_en">{i18n.language === 'ar' ? 'الملخص (إنجليزي)' : 'Excerpt (English)'}</Label>
                <Textarea
                  id="excerpt_en"
                  name="excerpt_en"
                  value={formData.excerpt_en}
                  onChange={handleChange}
                  rows={4}
                  dir="ltr"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="excerpt_ar">{i18n.language === 'ar' ? 'الملخص (عربي)' : 'Excerpt (Arabic)'}</Label>
                <Textarea
                  id="excerpt_ar"
                  name="excerpt_ar"
                  value={formData.excerpt_ar}
                  onChange={handleChange}
                  rows={4}
                  dir="rtl"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Selection */}
        <Card>
          <CardHeader>
            <CardTitle>{i18n.language === 'ar' ? 'الخدمة المرتبطة' : 'Related Service'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="service_id">{i18n.language === 'ar' ? 'اختر الخدمة' : 'Select Service'}</Label>
              <Select value={formData.service_id} onValueChange={handleServiceChange}>
                <SelectTrigger dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                  <SelectValue placeholder={i18n.language === 'ar' ? 'اختر خدمة...' : 'Select a service...'} />
                </SelectTrigger>
                <SelectContent dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                  {services.map((service: any) => (
                   <SelectItem key={service.id} value={service.id.toString()}>
  {i18n.language === 'ar' ? service.title_ar : service.title_en}
</SelectItem>

                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500">
                {i18n.language === 'ar' 
                  ? 'إذا لم تختار خدمة، سيتم ربط الكرت بأول خدمة متاحة تلقائياً'
                  : 'If no service is selected, the card will be automatically linked to the first available service'
                }
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
            <Save className="h-4 w-4 mr-2" />
            {createMutation.isPending || updateMutation.isPending
              ? (i18n.language === 'ar' ? 'جاري الحفظ...' : 'Saving...')
              : (isEdit 
                ? (i18n.language === 'ar' ? 'تحديث كرت الخدمة' : 'Update Service Card')
                : (i18n.language === 'ar' ? 'إنشاء كرت الخدمة' : 'Create Service Card')
              )
            }
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ServiceCardForm;
